/* subtitle-parser.js — shared SRT / VTT / ASS subtitle parser for Timbrica.
 *
 * Used by the language tools (subtitle-vocabulary, gap-fill-generator),
 * auto-captions, dialogue-enhancer, karaoke and /video-player.
 * Parses to a common cue shape: { index, start, end, text } with start/end
 * in seconds. Plain IIFE. Exposes window.SubtitleParser + module.exports.
 *
 * Two things a *player* needs that a text-miner does not, so both are opt-in
 * and the default output is byte-identical to what the older consumers got:
 *
 *   parse(text, { preserveMarkup: true })
 *     → cue.text keeps real line breaks ("- Hi\n- Bye" stays two lines)
 *       and cue.html carries a SANITIZED <i>/<b>/<u>/<br> subset.
 *     Without the flag, cue.text is still the single-line, tag-stripped
 *     string every existing caller expects.
 *
 *   decodeBytes(arrayBuffer) -> { text, encoding }
 *     Subtitle files in the wild are NOT always UTF-8 — Russian SRTs are
 *     routinely windows-1251, Western-European ones windows-1252. Reading
 *     those with the default decoder garbles every non-ASCII letter, which
 *     is exactly what a bare FileReader.readAsText() does.
 */
(function (root, factory) {
  'use strict';
  var S = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = S;
  if (typeof window !== 'undefined') window.SubtitleParser = S;
  else if (root) root.SubtitleParser = S;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ── encoding ──────────────────────────────────────────────────────────
     BOM first, then STRICT utf-8 (throws on the first invalid sequence),
     then a single-byte codepage picked by Cyrillic density. Ported from
     the sniffer in tools/subtitle-translator so the two agree. */
  function decodeBytes(buf) {
    if (typeof TextDecoder === 'undefined') {
      return { text: String(buf || ''), encoding: 'utf-8' };
    }
    var bytes = (buf instanceof Uint8Array) ? buf : new Uint8Array(buf);
    if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
      return { text: new TextDecoder('utf-8').decode(bytes.subarray(3)), encoding: 'utf-8' };
    }
    if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
      return { text: new TextDecoder('utf-16le').decode(bytes), encoding: 'utf-16le' };
    }
    if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
      return { text: new TextDecoder('utf-16be').decode(bytes), encoding: 'utf-16be' };
    }
    // UTF-16 without a BOM (Windows tools) is VALID UTF-8 byte-wise — every
    // other byte a NUL — so the strict test below would accept it and hand back
    // text full of control characters. Same structural test as
    // subtitle-convert/decode.js utf16Shape().
    var u16 = utf16Shape(bytes);
    if (u16) return { text: new TextDecoder(u16).decode(bytes), encoding: u16 };
    try {
      return { text: new TextDecoder('utf-8', { fatal: true }).decode(bytes), encoding: 'utf-8' };
    } catch (_) { /* not UTF-8 — fall through to legacy codepages */ }
    var cp = sniffCodepage(bytes);
    return { text: new TextDecoder(cp).decode(bytes), encoding: cp };
  }

  /* Choose between windows-1251 and windows-1252 for a byte stream that is not
   * UTF-8. Both codepages decode every byte, so there is no "invalid" signal to
   * lean on — the two produce different letters from the same bytes and one of
   * them is nonsense.
   *
   * WHY THE OBVIOUS TEST IS WRONG. Until 2026-08-19 this counted Cyrillic
   * letters and required more than 20. That is an absolute threshold against a
   * quantity that scales with file size: a three-name list from a Russian
   * spreadsheet ("Аня\nБоря\nВаня" — 11 letters) fell straight through to
   * windows-1252 and came out as mojibake, while the same text pasted twice
   * decoded fine. Short files are exactly where label lists and lyric snippets
   * live.
   *
   * WHY DENSITY-OF-CYRILLIC IS ALSO WRONG. The accented letters of French,
   * German and Spanish occupy the same 0xC0-0xFF byte range that Cyrillic
   * does — é is 0xE9, which is "й" in windows-1251. So "what share of the
   * high bytes look Cyrillic?" answers 100 % for "Café" too.
   *
   * WHAT ACTUALLY SEPARATES THEM is that Cyrillic high bytes come in RUNS —
   * a Russian word is high-byte from end to end — while Western accents sit
   * alone between ASCII letters. Mean run length is ~3-6 for Russian and
   * ~1.0-1.2 for French/German/Spanish, and it does not move with file size.
   * A share-of-bytes test was tried first and is not enough on its own:
   * "Café à côté" is 4 high bytes in 11, which clears any threshold low enough
   * to still admit real Russian. Run length settles it — those 4 bytes are
   * four runs of one.
   *
   * The honest boundary: below ~8 bytes there is no signal either way, and a
   * lone accented letter will read as Cyrillic. Neither a subtitle file nor a
   * label list is ever that short. */
  function sniffCodepage(bytes) {
    var total = bytes.length, high = 0, runs = 0, inRun = false;
    for (var i = 0; i < total; i++) {
      if (bytes[i] >= 0x80) {
        high++;
        if (!inRun) { runs++; inRun = true; }
      } else { inRun = false; }
    }
    if (!high) return 'windows-1252';           // pure ASCII: both agree

    var cp1251 = new TextDecoder('windows-1251').decode(bytes);
    var cyr = (cp1251.match(/[Ѐ-џ]/g) || []).length;

    // Guard against binary-ish input: if the high bytes do not overwhelmingly
    // read as Cyrillic letters, this is not Russian text whatever the ratio.
    if (cyr / high < 0.8) return 'windows-1252';

    var meanRun = high / runs;
    if (meanRun >= 1.8) return 'windows-1251';
    // Escape hatch for text that is almost entirely high bytes (a bare word,
    // a one-column list with no separators) where runs cannot be compared.
    return (high / total >= 0.55) ? 'windows-1251' : 'windows-1252';
  }

  /* Half the bytes NUL in one parity and more than 15 % control bytes: a text
     file has none below 0x20 except tab and line ends. */
  function utf16Shape(bytes) {
    if (bytes.length < 32 || bytes.length % 2) return null;
    var scan = Math.min(bytes.length, 8192), control = 0, i;
    for (i = 0; i < scan; i++) {
      var b = bytes[i];
      if (b < 0x20 && b !== 0x09 && b !== 0x0A && b !== 0x0D) control++;
    }
    if (control / scan < 0.15) return null;
    var even = {}, odd = {}, ne = 0, no = 0;
    for (i = 0; i + 1 < scan; i += 2) {
      if (!even[bytes[i]]) { even[bytes[i]] = 1; ne++; }
      if (!odd[bytes[i + 1]]) { odd[bytes[i + 1]] = 1; no++; }
    }
    if (no <= 4 && ne >= 8) return 'utf-16le';
    if (ne <= 4 && no >= 8) return 'utf-16be';
    return null;
  }

  /* Read a File/Blob with encoding sniffing. Returns Promise<{text, encoding}>. */
  function readFile(file) {
    return file.arrayBuffer().then(function (buf) { return decodeBytes(buf); });
  }

  /* "00:01:23,456" / "00:01:23.456" / "01:23.456" / "0:01:23" → seconds. The
     fraction is optional and DECIMAL whatever its length: Python's
     str(timedelta) writes "0:00:01.500000" and nothing at all on whole seconds. */
  function timeToSec(str) {
    var m = String(str).trim().match(/(?:(\d+):)?(\d{1,2}):(\d{1,2})(?:[.,](\d{1,9}))?/);
    if (!m) return 0;
    var h = parseInt(m[1] || '0', 10), mi = parseInt(m[2], 10),
        s = parseInt(m[3], 10), ms = m[4] ? Math.round(parseFloat('0.' + m[4]) * 1000) : 0;
    return h * 3600 + mi * 60 + s + ms / 1000;
  }

  /* strip inline markup: <i>, {b}, ASS overrides, speaker prefixes */
  function cleanText(t) {
    return String(t || '')
      .replace(/<[^>]+>/g, '')
      .replace(/\{[^}]*\}/g, '')
      .replace(/\\N/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* ── markup-preserving variant ─────────────────────────────────────────
     Keeps hard line breaks and the italic/bold/underline the release group
     actually meant (thoughts, voice-over, on-screen text). Everything else
     — ASS override blocks, unknown tags, raw <>& — is dropped or escaped,
     so the result is safe to hand to innerHTML. */
  var KEEP_TAG = /^(i|b|u)$/;
  var ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return ESC[c]; });
  }

  /* → { text, html } — text has real "\n", html has <br> + the kept tags. */
  function richText(raw) {
    var s = String(raw || '')
      .replace(/\{[^}]*\}/g, '')             // ASS override blocks {\an8}, {\pos(..)}
      .replace(/\\N/g, '\n')                 // ASS hard break
      .replace(/\\n/g, '\n')                 // ASS soft break — treat as hard
      .replace(/\\h/g, ' ')                  // ASS non-breaking space
      // WebVTT karaoke timestamps (<00:00:01.480>, every word of YouTube's
      // auto-captions): the tag rule below needs a letter after "<", so they
      // used to reach the player's overlay as visible text.
      .replace(/<\d{1,3}(?::\d{1,2}){1,2}\.\d{1,3}>/g, '');

    var html = '', plain = '', open = [];
    var re = /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
    var last = 0, m;
    while ((m = re.exec(s)) !== null) {
      var chunk = s.slice(last, m.index);
      html += escapeHtml(chunk); plain += chunk;
      last = re.lastIndex;
      var closing = m[1] === '/', tag = m[2].toLowerCase();
      if (!KEEP_TAG.test(tag)) continue;     // unknown tag → dropped entirely
      if (closing) {
        var at = open.lastIndexOf(tag);
        if (at >= 0) { html += '</' + tag + '>'; open.splice(at, 1); }
      } else {
        html += '<' + tag + '>'; open.push(tag);
      }
    }
    var tail = s.slice(last);
    html += escapeHtml(tail); plain += tail;

    // Normalise BEFORE closing the unterminated tags — otherwise a trailing
    // newline (every SRT block ends with one) turns into a <br> that lands
    // *inside* the auto-closed tag: "<i>text<br></i>".
    // Collapse runs of spaces/tabs but never the newlines.
    function norm(x) {
      return x.replace(/[ \t]+/g, ' ').replace(/[ \t]*\n[ \t]*/g, '\n').trim();
    }
    plain = norm(plain);
    html = norm(html).replace(/\n/g, '<br>');
    for (var i = open.length - 1; i >= 0; i--) html += '</' + open[i] + '>';
    return { text: plain, html: html };
  }

  function makeCue(idx, start, end, body, opts) {
    if (opts && opts.preserveMarkup) {
      var r = richText(body);
      if (!r.text) return null;
      return { index: idx, start: start, end: end, text: r.text, html: r.html };
    }
    var clean = cleanText(body);
    return clean ? { index: idx, start: start, end: end, text: clean } : null;
  }

  /* A cue is a timing line and the text under it, up to a blank line OR the
     next timing line — the WebVTT parsing rule, and the one that survives files
     with no blank line between cues. Hours and the fraction are optional
     ("00:01.000" is what FFmpeg and Whisper write for the first hour). It is the
     rule of public/js/subtitle-convert/read.js, and
     tests/js/subtitle-wild-corpus.test.mjs holds BOTH readers to one corpus of
     real-world files, so the two cannot drift apart again (they had: 37 of 46
     here against 46 there, 2026-10-01). */
  var TIMING_LINE = /^\s*(?:\d+\s+)?((?:\d+:)?\d{1,2}:\d{1,2}(?:[.,]\d{1,9})?)\s*-{2,}>\s*((?:\d+:)?\d{1,2}:\d{1,2}(?:[.,]\d{1,9})?)/;
  var COUNTER_LINE = /^\s*\d+\s*$/;

  /* spaceIsBlank: SRT treats a line of spaces as the gap between cues (hand-
     edited files); WebVTT treats it as TEXT — YouTube's auto-captions open every
     karaoke cue with one, and taking it for a gap dropped those cues. */
  function parseLines(lines, opts, spaceIsBlank) {
    var cues = [], idx = 0, i = 0;
    function blank(l) { return spaceIsBlank ? !l.trim() : l === ''; }
    while (i < lines.length) {
      var m = lines[i].match(TIMING_LINE);
      if (!m) { i++; continue; }
      var start = timeToSec(m[1]), end = timeToSec(m[2]);
      i++;
      var body = [];
      while (i < lines.length && !blank(lines[i]) && !TIMING_LINE.test(lines[i])) body.push(lines[i++]);
      // No blank line before the next cue: its counter sits under this text.
      if (i < lines.length && TIMING_LINE.test(lines[i]) && body.length && COUNTER_LINE.test(body[body.length - 1])) body.pop();
      // Join with "\n": a two-line dialogue is two lines on screen, not one
      // long run-on. The default (no preserveMarkup) path re-collapses it in
      // cleanText, so older callers see exactly what they saw before.
      var cue = makeCue(idx + 1, start, end, body.join('\n'), opts);
      if (cue) { cues.push(cue); idx++; }
    }
    return cues;
  }

  function parseSRT(text, opts) {
    /* \r\n and a bare \r (classic Mac) are both line ends. */
    var raw = String(text || '').replace(/\r\n?/g, '\n');
    return parseLines(raw.split('\n'), opts, true);
  }

  function parseVTT(text, opts) {
    var raw = String(text || '').replace(/\r\n?/g, '\n');
    // drop the WEBVTT header line and NOTE / STYLE / REGION blocks
    raw = raw.replace(/^\uFEFF?WEBVTT[^\n]*\n/, '');
    raw = raw.replace(/\n(NOTE|STYLE|REGION)[\s\S]*?(?=\n\s*\n|$)/g, '');
    return parseLines(raw.split('\n'), opts, false);
  }

  /* ── ASS / SSA ─────────────────────────────────────────────────────────
     Read the [Events] Format: line to locate Start/End/Text by name — Text
     is the last field and may itself contain commas, so it is taken as the
     remainder. Handled here rather than by shelling out to FFmpeg: an .ass
     file is text, and spinning up a ~30 MB WASM build to convert it is a
     30 MB download to do a regex's job. */
  function parseASS(text, opts) {
    var lines = String(text || '').replace(/\r/g, '').split('\n');
    var fmt = null, cues = [], idx = 0, inEvents = false;
    for (var i = 0; i < lines.length; i++) {
      var ln = lines[i];
      if (/^\s*\[/.test(ln)) { inEvents = /^\s*\[Events\]/i.test(ln); continue; }
      var fm = ln.match(/^\s*Format\s*:\s*(.+)$/i);
      if (fm) {
        if (inEvents) fmt = fm[1].split(',').map(function (x) { return x.trim().toLowerCase(); });
        continue;
      }
      var dm = ln.match(/^\s*Dialogue\s*:\s*(.+)$/i);
      if (!dm) continue;
      var f = fmt || ['layer', 'start', 'end', 'style', 'name',
                      'marginl', 'marginr', 'marginv', 'effect', 'text'];
      var si = f.indexOf('start'), ei = f.indexOf('end'), xi = f.indexOf('text');
      if (si < 0 || ei < 0 || xi < 0) continue;
      // split only up to the Text field; the rest (commas included) is Text
      var parts = dm[1].split(',');
      if (parts.length <= xi) continue;
      var head = parts.slice(0, xi);
      var body = parts.slice(xi).join(',');
      var cue = makeCue(idx + 1, timeToSec(head[si]), timeToSec(head[ei]), body, opts);
      if (cue) { cues.push(cue); idx++; }
    }
    return cues;
  }

  function detect(text, ext) {
    var t = String(text || '');
    var e = String(ext || '').toLowerCase().replace(/^\./, '');
    if (e === 'ass' || e === 'ssa') return 'ass';
    if (e === 'vtt') return 'vtt';
    if (e === 'srt') return 'srt';
    if (/^\uFEFF?WEBVTT/.test(t)) return 'vtt';
    if (/^\s*\[Script Info\]/i.test(t) || /^\s*Dialogue\s*:/im.test(t)) return 'ass';
    return 'srt';
  }

  /* Auto-detect format and parse. Also accepts plain text (one cue per line).
     opts: { preserveMarkup, ext } */
  function parse(text, opts) {
    var t = String(text || '');
    var kind = detect(t, opts && opts.ext);
    if (kind === 'ass') return parseASS(t, opts);
    if (kind === 'vtt') return parseVTT(t, opts);
    if (t.indexOf('-->') >= 0) return parseSRT(t, opts);
    // plain text fallback — split into pseudo-cues by line
    var cues = [], idx = 0;
    t.split(/\n+/).forEach(function (line) {
      var c = cleanText(line);
      if (c) cues.push({ index: ++idx, start: 0, end: 0, text: c });
    });
    return cues;
  }

  function toPlainText(cues) {
    return (cues || []).map(function (c) { return c.text; }).join(' ');
  }

  /* ── timing repair ─────────────────────────────────────────────────────
     A constant offset fixes "subs start N seconds late". It does NOT fix a
     framerate mismatch (23.976 vs 25), where the error grows with time and
     the end of a film is minutes out. Two anchors — "this line is spoken
     HERE" at two points — give scale + offset:  t' = t*rate + offset. */
  function retime(cues, rate, offset) {
    var r = (typeof rate === 'number' && isFinite(rate) && rate > 0) ? rate : 1;
    var o = (typeof offset === 'number' && isFinite(offset)) ? offset : 0;
    return (cues || []).map(function (c) {
      return {
        index: c.index, text: c.text, html: c.html,
        start: c.start * r + o, end: c.end * r + o
      };
    });
  }

  /* Solve rate+offset from two (subtitleTime → realTime) anchor pairs. */
  function solveDrift(fromA, toA, fromB, toB) {
    var df = fromB - fromA;
    if (!isFinite(df) || Math.abs(df) < 1e-6) return { rate: 1, offset: toA - fromA };
    var rate = (toB - toA) / df;
    return { rate: rate, offset: toA - fromA * rate };
  }

  return {
    parse: parse,
    parseSRT: parseSRT,
    parseVTT: parseVTT,
    parseASS: parseASS,
    detect: detect,
    timeToSec: timeToSec,
    cleanText: cleanText,
    richText: richText,
    decodeBytes: decodeBytes,
    readFile: readFile,
    retime: retime,
    solveDrift: solveDrift,
    toPlainText: toPlainText
  };
});
